// src/components/ChatInterface.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";
import {
  Box,
  Dialog,
  Snackbar,
  Alert,
  Button,
  Typography,
} from "@mui/material";
import { InstallMobile } from "@mui/icons-material";

import { VirtualizedMessageList } from "./VirtualizedMessageList";
import { VisitNotification } from "./VisitNotification";
import { Composer } from "./chat/Composer";
import { WelcomeScreen } from "./chat/WelcomeScreen";
import { AttachmentStrip } from "./chat/AttachmentStrip";
import { SuggestionsStrip } from "./chat/SuggestionsStrip";
import { WhisperBanner } from "./whispers/WhisperBanner";
import { StreamingBanner } from "./chat/StreamingBanner";
import { ErrorRetryBar } from "./chat/ErrorRetryBar";

import { useChat } from "../hooks/useChat";
import { useLocation } from "../hooks/useLocation";
import { useVisits } from "../hooks/useVisits";
import { useFreshLocation } from "../hooks/useFreshLocation";
import { useAttachments } from "../hooks/useAttachments";
import { writeFeedItem } from "../feed/writeFeedItem";
import { setTrackingConversationId } from "../services/locationTrackingService";
import { IMAGE_TOUR_GUIDE_PROMPT, IMAGE_MUSEUM_GUIDE_PROMPT } from "../constants/prompts";
import { Location } from "../types";
import {
  useConversationStore,
  ConversationType,
} from "../stores/conversationStore";
import { WalkingTourView } from "./walkingTour/WalkingTourView";
import { MuseumGuideView } from "./museumGuide/MuseumGuideView";
import { MapExplorationView } from "./map/MapExplorationView";
import { useWalkingTour } from "../hooks/useWalkingTour";
import { useCreateMapExploration } from "../hooks/useCreateMapExploration";
import { useT } from '../i18n/useT';

interface ChatInterfaceProps {
  conversationId: string | null;
  onConversationCreated: (conversationId: string) => void;
}

interface FailedMessage {
  message: string;
  displayMessage?: string;
  imagePath?: string;
  location?: Location;
  isLocationRequest: boolean;
  isImageOnlySubmission: boolean;
  /** When the failed send was the FIRST message in a new conversation
   *  and the user had picked a mode on the WelcomeScreen, we keep the
   *  seed so a retry creates the conversation in the right mode. */
  initialMode?: ConversationType;
  initialModeData?: Record<string, any>;
}

/**
 * The chat view. Owns composer state, image upload state, camera modal,
 * retry of failed sends, and wiring to useChat. All heavy lifting
 * (streaming, persistence, landmark verification) lives in useChat.
 */
export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  conversationId,
  onConversationCreated,
}) => {
  const t = useT();
  const [message, setMessage] = useState("");
  // Native-camera flow:
  //   • cameraInputRef points at a hidden <input type="file" capture>
  //     element. Tapping the camera button in the Composer triggers
  //     a click on it, which delegates to the OS Camera app on
  //     mobile (Photos/Files picker on desktop without a camera).
  //   • cameraUnavailableMsg is set when we want to surface a
  //     fallback dialog (e.g. legacy browser without file-input
  //     capture support, or the user explicitly tells us). Empty
  //     string = hidden.
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [cameraUnavailableMsg, setCameraUnavailableMsg] = useState<
    string | null
  >(null);
  // "Post to feed" toggle for the photo flow. When true at send
  // time, ChatInterface writes a feed_item per completed image
  // attachment after the message lands. Visibility uses the user's
  // photo default (public unless they've customized it).
  const [postToFeed, setPostToFeed] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isRequestingLocationInfo, setIsRequestingLocationInfo] =
    useState(false);
  const [failedMessage, setFailedMessage] = useState<FailedMessage | null>(
    null
  );

  // Mode the user has tapped on the WelcomeScreen but not yet
  // committed by sending a first message. Null means "no preference"
  // (fall back to standard on send). Resets whenever the conversation
  // id changes — leaving and coming back to /chat (no id) gives the
  // user a fresh picker.
  const [welcomeMode, setWelcomeMode] = useState<ConversationType | null>(
    null
  );

  const { location } = useLocation();
  const { getFreshLocation } = useFreshLocation();
  const { latestVisit, clearLatestVisit } = useVisits();

  // Walking-tour generator. Fired by the snapshot-driven effect
  // below when the conversation's walking_tour.status flips to
  // 'generating' — which happens after the chat AI captures the
  // user's chosen focus via a [WALKING_TOUR_FOCUS] state marker.
  // Both walking_tour and museum_guide now use AI-driven onboarding
  // for the initial state machine; this generator is just the
  // dedicated waypoint-with-coords producer that runs once the
  // user's focus is known.
  const { generate: generateWalkingTour } = useWalkingTour();

  // Map exploration generator — creates the place list for a
  // map_exploration conversation. Fires from an effect below
  // when map_exploration.status flips to 'generating'. Same
  // shape as useWalkingTour: HTTP onRequest, returns 202, writes
  // results back to the conversation doc. See MAP_NOTES.md.
  const { generate: generateMapExploration } = useCreateMapExploration();

  // Pluck the active conversation from the store. Live snapshot —
  // when the conversation flips to walking_tour or museum_guide mode,
  // this updates.
  const conversations = useConversationStore((s) => s.conversations);
  const activeConversation = useMemo(
    () =>
      conversationId
        ? conversations.find((c) => c.id === conversationId) || null
        : null,
    [conversations, conversationId]
  );
  const isWalkingTour = activeConversation?.type === "walking_tour";
  const isMuseumGuide = activeConversation?.type === "museum_guide";
  const isMapExploration = activeConversation?.type === "map_exploration";

  const {
    messages,
    isLoading,
    sendMessage,
    isSending,
    isStreaming,
    streamingContent,
    streamingStatus,
    error,
    conversationId: actualConversationId,
    pendingIndicator,
    clearError,
  } = useChat(conversationId);

  // Stable conversation id to hand to the camera + attachments
  const cameraConversationId = useMemo(() => {
    if (conversationId) return conversationId;
    if (actualConversationId) return actualConversationId;
    return `temp_${Date.now()}`;
  }, [conversationId, actualConversationId]);

  // Multi-attachment manager — each tile uploads independently with
  // its own retry/error state, and the user can send only when at
  // least one is complete and none are still in flight.
  const {
    attachments,
    isAnyInFlight,
    hasCompleted,
    completedPaths,
    add: addAttachments,
    remove: removeAttachment,
    retry: retryAttachment,
    clear: clearAttachments,
  } = useAttachments(cameraConversationId);

  // Surface newly-created conversations to the router
  useEffect(() => {
    if (actualConversationId && actualConversationId !== conversationId) {
      onConversationCreated(actualConversationId);
    }
  }, [actualConversationId, conversationId, onConversationCreated]);

  // Reset the WelcomeScreen mode pick when navigating between
  // conversations. Without this, going from /chat/<old> back to /chat
  // (e.g. via the sidebar's New Conversation button) would carry over
  // a stale selection that the user can't see (the WelcomeScreen
  // re-mounts and would render the old state highlighted). Clearing
  // on every conversation transition keeps the picker honest.
  useEffect(() => {
    setWelcomeMode(null);
  }, [conversationId]);

  // ── Walking-tour generator trigger ─────────────────────────────
  // The walking_tour onboarding flow:
  //   1. WelcomeScreen tap → handleCommit → conversation created in
  //      status='awaiting_focus' + first user message.
  //   2. AI sees the state, suggests local route ideas as chips.
  //   3. User picks/types focus → next AI turn emits
  //      [CONVERSATION_STATE]{"status":"generating","focus":"…"}[/...]
  //      marker. Server applies the marker — walking_tour.status
  //      flips to 'generating' and walking_tour.focus is captured.
  //   4. THIS effect detects the transition and fires the dedicated
  //      generateWalkingTour cloud function with the captured focus.
  //
  // The same effect ALSO handles regeneration. When the AI emits
  // a regenerate_tour marker (theme change), the server flips
  // status back to 'generating' and writes a NEW focus. The dedup
  // key includes the focus, so a focus change is a fresh key and
  // re-fires generateWalkingTour. Old waypoints stay on disk
  // until the generator overwrites them — this gives the user a
  // graceful loader experience and keeps the old tour intact if
  // generation fails.
  //
  // Dedup is per (conversation, focus) so:
  //   • Initial generation fires once.
  //   • Snapshot churn during generation doesn't re-fire.
  //   • A regenerate (new focus) DOES fire (new key).
  const firedWalkingTourGenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeConversation) return;
    if (activeConversation.type !== 'walking_tour') return;
    const wt = activeConversation.walking_tour;
    if (!wt) return;
    // Error unlocks the dedup key for that focus. Without this, a
    // failed generation followed by the user re-picking the SAME
    // theme flips status back to 'generating' but the key is
    // already in the set — the function never re-fires and the
    // loader spins forever (until a full remount). Clearing on
    // error makes the retry loop actually close.
    // Dedup key includes the server-bumped generation_seq so that
    // re-picking a PREVIOUS focus (A → B → A) is still a fresh key
    // and re-fires. The seq is 0 for initial generations seeded
    // client-side and increments on every server-applied
    // CONVERSATION_STATE → 'generating' transition.
    const wtSeq = wt.generation_seq ?? 0;
    if (wt.status === 'error' && wt.focus) {
      firedWalkingTourGenRef.current.delete(
        `${activeConversation.id}:${wt.focus}:${wtSeq}`
      );
      return;
    }
    if (wt.status !== 'generating') return;
    // Need a focus to generate against — if absent, bail and let
    // the user try again via chat.
    if (!wt.focus) return;
    const dedupKey = `${activeConversation.id}:${wt.focus}:${wtSeq}`;
    if (firedWalkingTourGenRef.current.has(dedupKey)) return;

    firedWalkingTourGenRef.current.add(dedupKey);
    void generateWalkingTour({
      conversationId: activeConversation.id,
      focus: wt.focus,
    });
  }, [activeConversation, generateWalkingTour]);

  // ── Map-exploration generation trigger ──────────────────────────
  // Watches the conversation snapshot for a map_exploration in the
  // 'generating' state and fires the createMapExploration cloud
  // function once. The cloud function does the AI + Places work
  // and writes the result back; the snapshot listener picks it up
  // and the view re-renders.
  //
  // The 'generating' state is initially seeded by handleSend when
  // the user's first message ships in map_exploration mode (no
  // AI-driven onboarding step — the user's prompt IS the query).
  // Phase 2 will add 'expand'/'replace' refinements that re-enter
  // 'generating' from later turns; this same effect will fire them.
  //
  // Dedup is per (conversation, query) so:
  //   • Initial generation fires once.
  //   • Snapshot churn during generation doesn't re-fire.
  //   • A new query (refinement) DOES fire (new key).
  const firedMapExplorationGenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeConversation) return;
    if (activeConversation.type !== 'map_exploration') return;
    const me = activeConversation.map_exploration;
    if (!me) return;
    // Same error-unlock as the walking-tour trigger above: a
    // failed run must release the (conversation, query) key or a
    // same-query retry can never re-fire.
    // Same seq-in-key treatment as the walking-tour trigger: a
    // refinement back to an earlier query must still re-fire.
    const meSeq = me.generation_seq ?? 0;
    if (me.status === 'error' && me.last_query) {
      firedMapExplorationGenRef.current.delete(
        `${activeConversation.id}:${me.last_query}:${meSeq}`
      );
      return;
    }
    if (me.status !== 'generating') return;
    // Need a query to generate against — without one we can't tell
    // the cloud function what to search for. Bail; the user can
    // retry by typing.
    if (!me.last_query) return;
    const dedupKey = `${activeConversation.id}:${me.last_query}:${meSeq}`;
    if (firedMapExplorationGenRef.current.has(dedupKey)) return;

    firedMapExplorationGenRef.current.add(dedupKey);
    void generateMapExploration({
      conversationId: activeConversation.id,
      query: me.last_query,
    });
  }, [activeConversation, generateMapExploration]);

  // ── Tour-ready opener ────────────────────────────────────────────
  // Once the tour generator finishes (status flips to 'ready' and
  // waypoints exist) AND the user hasn't started walking yet (no
  // waypoint has arrived_at, none are visited), fire a synthetic
  // "Let's begin the tour." message so the AI introduces the first
  // stop — that's the entry point of the turn-taking flow.
  //
  // Dedup keyed by (conversationId, focus) so:
  //   • Initial generation fires the opener once.
  //   • Snapshot churn during the user's tour doesn't re-fire.
  //   • A regenerate (new focus, same conversation) → new key →
  //     opener fires again for the new tour.
  //
  // Pristine-state gate is essential: if the user is returning to
  // a half-walked tour, waypoints will already have arrived_at
  // and/or visited statuses, and we must NOT inject "Let's begin"
  // — they've already begun.
  const firedTourOpenerRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeConversation) return;
    if (activeConversation.type !== 'walking_tour') return;
    const wt = activeConversation.walking_tour;
    if (!wt) return;
    if (wt.status !== 'ready') return;
    if (!wt.waypoints || !wt.waypoints.length) return;
    if (!wt.focus) return;
    if (wt.completed_at) return;
    // Pristine: no waypoint has arrived_at, none are visited or
    // skipped. If any of those conditions fail the user is
    // mid-tour and the AI continues from wherever they left off
    // (the existing prompt flow handles that).
    const pristine = wt.waypoints.every(
      (w) =>
        !w.arrived_at &&
        w.status !== 'visited' &&
        w.status !== 'skipped'
    );
    if (!pristine) return;
    if (isSending || isStreaming) return;
    const dedupKey = `${activeConversation.id}:${wt.focus}`;
    if (firedTourOpenerRef.current.has(dedupKey)) return;

    firedTourOpenerRef.current.add(dedupKey);
    void sendMessage({
      message: "Let's begin the tour.",
      displayMessage: "Let's begin the tour.",
      location: undefined,
      isLocationRequest: false,
      isImageOnlySubmission: false,
    });
  }, [activeConversation, isSending, isStreaming, sendMessage]);

  // Keep location-tracker in sync with active conversation
  useEffect(() => {
    setTrackingConversationId(actualConversationId || null);
  }, [actualConversationId]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setShowInstallPrompt(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // --- Auto-prompt handoff from Guide page ---
  // When the user taps a "Tell me about this" button on the Guide tab,
  // GuidePage navigates here with an autoPrompt in either nav state or
  // sessionStorage. We pick it up, fill the composer, and auto-send.
  const routerLocation = useRouterLocation();
  const autoSendPromptRef = useRef<string | null>(null);

  useEffect(() => {
    // Only consume on actual nav event (not on every render)
    const navState = (routerLocation.state as any) || null;
    const storagePrompt = sessionStorage.getItem("instantTours:autoPrompt");
    const autoPrompt = navState?.autoPrompt || storagePrompt;

    if (!autoPrompt) return;

    // Consume the prompt source so refresh/back-nav doesn't refire it
    sessionStorage.removeItem("instantTours:autoPrompt");
    if (navState?.autoPrompt) {
      window.history.replaceState({}, "");
    }

    autoSendPromptRef.current = autoPrompt;
    setMessage(autoPrompt);
    // handleSend fires in the next effect once `message` is in state
    // and `isSending`/`isStreaming` are clear.
  }, [routerLocation.key]);

  // When the autoPrompt is in state AND we're idle, send it once.
  useEffect(() => {
    if (!autoSendPromptRef.current) return;
    if (message !== autoSendPromptRef.current) return;
    if (isSending || isStreaming) return;

    autoSendPromptRef.current = null;
    // Microtask defer so `message` has definitely propagated through
    // handleSend's closure. Calling handleSend inline sometimes
    // still reads the previous message from the captured closure.
    Promise.resolve().then(() => {
      handleSendRef.current?.();
    });
  }, [message, isSending, isStreaming]);

  // Ref-held handleSend so the auto-send effect can call it without
  // needing handleSend in its deps (which would cause stale-closure
  // loops with the message string).
  const handleSendRef = useRef<() => Promise<void>>();

  // --- Send a message ---
  const handleSend = useCallback(async () => {
    if (!message.trim() && !hasCompleted) return;
    if (isSending || isStreaming) return;
    // Block send while any attachment is still uploading; UI also
    // disables the button but defense-in-depth.
    if (isAnyInFlight) return;

    const userText = message.trim();
    const fresh = await getFreshLocation();
    const pathsToSend = [...completedPaths];
    const isImageOnly = pathsToSend.length > 0 && !userText;

    // Send the FIRST image as the canonical `imagePath` for backward
    // compat (verifyLandmark path, Vision API call). Additional
    // images will be plumbed through to the chat AI as a separate
    // payload field once the backend supports it. For now they're
    // captured client-side and persisted as part of the message
    // content array.
    const primaryImagePath = pathsToSend[0];

    // Pick the image-only fallback prompt based on mode. Museum mode
    // wants exhibit-identification framing; everything else uses the
    // generic tour-guide prompt (the system prompt already adds
    // mode-state context for tour conversations).
    const imageFallbackPrompt =
      isMuseumGuide || welcomeMode === 'museum_guide'
        ? IMAGE_MUSEUM_GUIDE_PROMPT
        : IMAGE_TOUR_GUIDE_PROMPT;

    // If this is the first send in a brand-new conversation AND the
    // user picked a mode on the WelcomeScreen, build the seed that
    // createConversation will write to the doc. Without a route id
    // and no existing conversation, ensureConversation creates a new
    // doc; the seed determines what mode it lands in.
    //
    // Standard needs no seed — we just don't pass anything and the
    // doc is created without a `type` field (snapshot mapper falls
    // back to 'standard').
    //
    // Walking tour and Museum guide normally come through
    // handleCommit (tap-to-start), but if the user typed something
    // first and welcomeMode is set we still honour the mode pick
    // here — seed with the AI-driven onboarding state. The user's
    // typed text becomes the first message; the AI handles the
    // state machine from there.
    //
    // Map exploration is type-then-send: the user's first message
    // IS the search query, no clarification phase needed. We seed
    // map_exploration.status='generating' directly so the trigger
    // effect above fires the createMapExploration cloud function
    // as soon as the snapshot lands.
    let initialMode: ConversationType | undefined;
    let initialModeData: Record<string, any> | undefined;
    const isFirstSendOfNewConversation = !conversationId;
    if (isFirstSendOfNewConversation && welcomeMode) {
      if (welcomeMode === 'walking_tour') {
        initialMode = 'walking_tour';
        initialModeData = {
          status: 'awaiting_focus',
          focus: userText || null,
        };
      } else if (welcomeMode === 'museum_guide') {
        initialMode = 'museum_guide';
        initialModeData = { status: 'awaiting_museum' };
      } else if (welcomeMode === 'map_exploration' && userText) {
        initialMode = 'map_exploration';
        initialModeData = {
          status: 'generating',
          last_query: userText,
          // Server fills filters during ranking. Empty places is
          // the correct seed; the snapshot mapper falls back to
          // pref defaults when filters is absent.
          places: [],
        };
      }
    }

    const payload: FailedMessage = {
      message: userText || imageFallbackPrompt,
      displayMessage: isImageOnly ? "" : userText,
      imagePath: primaryImagePath,
      location: fresh || undefined,
      isLocationRequest: false,
      isImageOnlySubmission: isImageOnly,
      initialMode,
      initialModeData,
    };

    setFailedMessage(payload);
    setMessage("");
    // Capture toggle + paths BEFORE clearAttachments resets state.
    // The post-send feed write fires later and would see the cleared
    // values otherwise.
    const shouldPostToFeed = postToFeed && pathsToSend.length > 0;
    const feedPaths = [...pathsToSend];
    const feedCaption = userText;
    const feedLocation = fresh
      ? {
          lat: fresh.latitude,
          lng: fresh.longitude,
        }
      : undefined;
    // Clear the strip immediately so the user sees the composer
    // reset to "ready for next message" while sendMessage runs.
    clearAttachments();
    setPostToFeed(false);

    try {
      // sendMessage returns the conversation id (the freshly-created
      // one if this was a new conversation, or the existing one
      // otherwise). We use that directly rather than reading
      // actualConversationId from the closure — the closure value
      // was captured when handleSend was created (likely null for a
      // new conversation), and React state may not have flushed by
      // the time the await resolves.
      await sendMessage(payload);
      setFailedMessage(null);

      // Post-send: if the user toggled "post to feed", create a
      // feed_item per completed image. Visibility comes from the
      // user's photo default (public unless customized). Fire-and-
      // forget — feed write failure shouldn't surface as a
      // user-visible error since the chat reply already succeeded.
      if (shouldPostToFeed) {
        for (const path of feedPaths) {
          void writeFeedItem({
            kind: 'photo',
            snapshot: {
              caption: feedCaption || undefined,
              image_path: path,
              preview_url: path,
              location: feedLocation,
            },
            source: { kind: 'manual', id: path },
          });
        }
      }

      // generateWalkingTour is now fired by an effect that watches
      // the conversation snapshot for the AI-emitted transition
      // walking_tour.status → 'generating'. See the effect below.
      // We deliberately don't fire from here anymore; the AI
      // captures the user's chosen focus via a state marker first.
    } catch {
      // Stay in failedMessage for retry — but note that the
      // attachments are already cleared from UI state. The retry
      // path uses `failedMessage.imagePath` which is preserved.
    }
  }, [
    message,
    hasCompleted,
    isAnyInFlight,
    completedPaths,
    isSending,
    isStreaming,
    getFreshLocation,
    sendMessage,
    clearAttachments,
    isMuseumGuide,
    welcomeMode,
    conversationId,
    postToFeed,
  ]);

  // Keep the ref in sync so auto-send always calls the freshest handleSend
  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  const handleRetry = useCallback(async () => {
    if (!failedMessage || isSending || isStreaming) return;
    clearError?.();
    try {
      await sendMessage(failedMessage);
      setFailedMessage(null);
    } catch {
      // keep failedMessage for another retry
    }
  }, [failedMessage, isSending, isStreaming, sendMessage, clearError]);

  // Tap-to-start handler for modes where the AI takes the lead
  // immediately. Two modes use this:
  //   • museum_guide → AI greets and asks which museum (with nearby
  //                    chips fetched from Places).
  //   • walking_tour → AI greets and surfaces local route ideas
  //                    based on the user's location + profile, then
  //                    captures the user's pick via a state marker
  //                    that triggers generateWalkingTour.
  //
  // For both, we synthesise a brief, natural user message so the
  // chat history starts coherently. The AI's mode-specific system
  // prompt branch handles the rest (greeting, suggestions chips,
  // state-machine markers).
  //
  // Standard intentionally still uses the type-then-send pattern —
  // it has no onboarding state machine to drive.
  const handleCommit = useCallback(
    async (mode: ConversationType) => {
      if (mode !== 'museum_guide' && mode !== 'walking_tour') return;
      if (isSending || isStreaming) return;
      // Don't fire if a conversation already exists in this view.
      if (conversationId) return;

      setWelcomeMode(mode);

      const fresh = await getFreshLocation();
      const kickoffMessage =
        mode === 'museum_guide'
          ? "Help me explore a museum."
          : "Help me plan a walking tour.";

      const initialModeData =
        mode === 'museum_guide'
          ? { status: 'awaiting_museum' }
          : { status: 'awaiting_focus' };

      const payload: FailedMessage = {
        message: kickoffMessage,
        displayMessage: kickoffMessage,
        location: fresh || undefined,
        isLocationRequest: false,
        isImageOnlySubmission: false,
        initialMode: mode,
        initialModeData,
      };

      setFailedMessage(payload);
      try {
        await sendMessage(payload);
        setFailedMessage(null);
      } catch {
        // Keep failedMessage so the error bar offers retry.
      }
    },
    [isSending, isStreaming, conversationId, getFreshLocation, sendMessage]
  );

  const handleDismissError = useCallback(() => {
    setFailedMessage(null);
    clearError?.();
  }, [clearError]);

  // --- "Explore where I am" ---
  const handleExplore = useCallback(async () => {
    if (isSending || isStreaming || isRequestingLocationInfo) return;
    setIsRequestingLocationInfo(true);
    try {
      const fresh = await getFreshLocation();
      if (!fresh) {
        console.error("No location available");
        return;
      }
      const payload: FailedMessage = {
        message:
          "Tell me something specific and interesting about where I'm standing right now. What's around me that I'd actually want to know about — not a generic neighborhood overview. Lead with the single most interesting thing.",
        displayMessage: "",
        location: fresh,
        isLocationRequest: true,
        isImageOnlySubmission: false,
      };
      setFailedMessage(payload);
      try {
        await sendMessage(payload);
        setFailedMessage(null);
      } catch {
        // retry available
      }
    } finally {
      setIsRequestingLocationInfo(false);
    }
  }, [
    isSending,
    isStreaming,
    isRequestingLocationInfo,
    getFreshLocation,
    sendMessage,
  ]);

  // --- Interactive markup & follow-up clicks from messages ---
  const handleInteractiveClick = useCallback(
    async (followUpMessage: string, _element?: any) => {
      if (isSending || isStreaming) return;
      const fresh = await getFreshLocation();
      await sendMessage({
        message: followUpMessage,
        displayMessage: "",
        location: fresh || undefined,
        isLocationRequest: false,
        isImageOnlySubmission: false,
      });
    },
    [isSending, isStreaming, getFreshLocation, sendMessage]
  );

  const handleFollowUpClick = useCallback(
    async (prompt: string) => {
      if (isSending || isStreaming) return;
      const fresh = await getFreshLocation();
      await sendMessage({
        message: prompt,
        displayMessage: prompt,
        location: fresh || undefined,
        isLocationRequest: false,
        isImageOnlySubmission: false,
      });
    },
    [isSending, isStreaming, getFreshLocation, sendMessage]
  );

  // Native camera flow.
  //
  // Tapping the Composer's camera button calls handleOpenNativeCamera,
  // which programmatically clicks a hidden <input type="file"
  // accept="image/*" capture="environment">. iOS/Android route this
  // to the OS Camera app (or the user can pick a recent photo);
  // desktop opens the file picker.
  //
  // Permissions: the input element doesn't ask for browser camera
  // permission. The OS handles permission flows in its own UI when
  // the camera app launches. If the user has system-level camera
  // permission denied for the OS or for the browser, the OS will
  // show its own re-prompt or send them to Settings — outside our
  // control, which is the correct UX.
  //
  // Failure modes we DO handle here:
  //   • Older browser without input-capture support → fall back to
  //     the standard photo-attach flow (same as the paperclip
  //     button), surfaced via a friendly dialog.
  //   • The input ref isn't mounted (shouldn't happen in practice
  //     given a single render, but defensive).
  //   • The file-picker returns nothing (user cancelled) → no-op.
  //
  // Result handling: same path as gallery picks — addAttachments(files)
  // runs the upload pipeline, the AttachmentStrip renders progress,
  // user reviews and taps send.
  const handleOpenNativeCamera = useCallback(() => {
    const input = cameraInputRef.current;
    if (!input) {
      // Defensive: should never happen because the input is always
      // rendered. If it does, point the user at the gallery flow.
      setCameraUnavailableMsg(
        t("Couldn't open the camera. You can attach a photo from your library instead.")
      );
      return;
    }
    // Reset the value so picking the same file twice still fires
    // the change event. Without this, iOS treats the second
    // selection as a no-op.
    try {
      input.value = '';
    } catch {
      // Ignore — some older Safari versions throw on programmatic
      // value assignment but the click below still works.
    }
    input.click();
  }, [t]);

  const handleNativeCameraChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      // The standard attachments pipeline handles validation,
      // upload, retries, progress, HEIC, and storage paths.
      addAttachments(files);
      // Clear the input value so the user can pick the same file
      // again later if needed.
      try {
        e.target.value = '';
      } catch {
        // ignore
      }
    },
    [addAttachments]
  );

  const isDisabled = isSending || isStreaming;

  // Welcome-screen gate. Originally checked a long list of "is this
  // conversation busy?" flags (isStreaming, streamingContent,
  // isSending, isLoading) on top of the no-conversation check. The
  // problem: those flags live in useStreamingChat / useChat and are
  // NOT reset when the user navigates away mid-stream. So clicking
  // "New conversation" while the previous chat was still streaming
  // would arrive at /chat with stale isStreaming=true → welcome
  // gate fails → user sees a blank message list instead of the
  // mode picker. (Intermittent because it only happens when the
  // user navigates during an active stream.)
  //
  // Correct semantic: when there's no conversation context (no URL
  // id and no locally-created id), the welcome screen should show.
  // Stale streaming flags belong to a different (previous)
  // conversation context and are irrelevant here. messages.length
  // is checked as a final guard against showing welcome over a
  // conversation that's mid-creation but already has the kickoff
  // message rendered.
  //
  // pendingIndicator is intentionally NOT checked — it's already
  // cleared by resetConversationState on route change.
  const showWelcomePage =
    !conversationId &&
    !actualConversationId &&
    messages.length === 0;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        // position: relative so the map-minimize FAB can anchor to
        // the chat container's top-left corner — and so the
        // absolute-positioned bottom stack (composer + strips) below
        // anchors against this Box.
        position: "relative",
        // No padding-top OR padding-bottom: the message list extends
        // the entire column height so content scrolls under both
        // floating frosted bars. The composer + strips are pulled
        // out of the flex flow into an absolute stack just above
        // the bottom nav (see below).
      }}
    >
      {/* Walking tour map + waypoint header — fills the top half of
          the screen (50vh) when this conversation is in tour mode
          and the map is not minimized. The view itself guards on
          status (renders nothing during awaiting_focus, the chat
          takes the full screen while the AI gathers route ideas)
          and owns the minimize/expand FAB internally. Chat below
          stays scrollable with the composer pinned at the bottom,
          preserving normal chat ergonomics. */}
      {isWalkingTour && activeConversation && (
        <WalkingTourView
          conversation={activeConversation}
          onSendMessage={(text) => {
            // Block during in-flight messages — synthetic signals
            // shouldn't queue up while the AI is mid-response. The
            // user can re-tap once the previous response settles.
            if (isSending || isStreaming) return;
            void sendMessage({
              message: text,
              displayMessage: text,
              location: undefined,
              isLocationRequest: false,
              isImageOnlySubmission: false,
            });
          }}
        />
      )}

      {/* Museum guide top panel — exhibit list (guided) or status
          strip (free roam). Mutually exclusive with WalkingTourView
          since a conversation has exactly one type. The view itself
          guards on status and renders nothing during the AI-driven
          onboarding states (awaiting_museum / awaiting_submode), so
          the chat occupies the full screen while the user is still
          picking a museum. */}
      {isMuseumGuide && activeConversation && (
        <MuseumGuideView conversation={activeConversation} />
      )}

      {/* Map exploration top panel — drawer + map + place pins.
          Mutually exclusive with the other top-half views. Mounts
          on every status (generating / ready / error) and handles
          its own loading + error states inline. The chat below
          remains scrollable and the composer accepts refinement
          messages (Phase 2 will route those through an ensemble
          classifier; for now they go to streamTourGuide as
          standard chat turns). */}
      {isMapExploration && activeConversation && (
        <MapExplorationView conversation={activeConversation} />
      )}

      {/* Message area */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {showWelcomePage ? (
          <WelcomeScreen
            selectedMode={welcomeMode}
            onSelectMode={setWelcomeMode}
            onCommit={handleCommit}
          />
        ) : (
          <VirtualizedMessageList
            messages={messages}
            isLoading={isLoading}
            isStreaming={isStreaming}
            streamingContent={streamingContent}
            streamingStatus={streamingStatus}
            onInteractiveClick={handleInteractiveClick}
            onFollowUpClick={handleFollowUpClick}
            pendingIndicator={pendingIndicator}
            isSending={isSending}
            userLocation={
              location?.latitude && location?.longitude
                ? { lat: location.latitude, lng: location.longitude }
                : undefined
            }
            conversationId={conversationId || actualConversationId || undefined}
          />
        )}
      </Box>

      {/* Bottom stack: streaming banner, error bar, strips, and
          composer — pulled out of flex flow into an absolute
          cluster that floats just above the AppShell's frosted
          bottom nav. This is what gives the bottom nav something
          varied to blur: the messages list (above) extends full
          column height, scrolls under this cluster, and continues
          under the bottom nav. The cluster itself is opaque (each
          child has its own bg) so it visually separates from the
          messages, while the area BELOW it (the bottom-nav region)
          shows scrolled message content blurred through glass. */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 'var(--app-bottom-nav-h)',
          zIndex: 5,
        }}
      >
        {/* Streaming banner */}
        {isStreaming && <StreamingBanner status={streamingStatus} />}

        {/* Error bar (inline, replaces the two-button snackbar) */}
        {error && (
          <ErrorRetryBar
            message={error.message || "Failed to send message"}
            canRetry={!!failedMessage}
            onRetry={handleRetry}
            onDismiss={handleDismissError}
            disabled={isSending || isStreaming}
          />
        )}

        {/* Attachment strip — multiple thumbnails, each uploading independently */}
        <AttachmentStrip
          attachments={attachments}
          onRemove={removeAttachment}
          onRetry={retryAttachment}
          postToFeed={postToFeed}
          onTogglePostToFeed={setPostToFeed}
        />

        {/* Mode-conversion CTAs intentionally omitted. Mode is picked
            at conversation creation on the WelcomeScreen; mid-stream
            mode-switching may come back later but isn't supported now.
            See WelcomeScreen.tsx for the entry point. */}

        {/* Suggestions strip — visible only when the conversation
            hasn't started yet (no messages). Once any message exists
            we hide it; the suggestions are starters, not in-flight
            prompts. Also gated on empty composer + no attachments
            (don't compete with what the user is in the middle of). */}
        <SuggestionsStrip
          onPick={(text) => setMessage(text)}
          visible={
            messages.length === 0 &&
            message.trim().length === 0 &&
            attachments.length === 0
          }
        />

        {/* Whisper banner — single-line nudge the server writes when
            the user dwells near an unfound collection-worthy item.
            Sits between suggestions and composer so it's visually
            adjacent to the input the user would tap to engage.
            Renders nothing when there's no active whisper. */}
        <WhisperBanner
          onAsk={(prompt) => {
            setMessage(prompt);
            // Defer to next tick so the textbox re-renders with the
            // value before send fires.
            setTimeout(() => {
              handleSend();
            }, 0);
          }}
        />

        {/* Composer */}
        <Composer
          value={message}
          onChange={setMessage}
          onSend={handleSend}
          onOpenCamera={handleOpenNativeCamera}
          onExplore={handleExplore}
          onAttachFiles={(files) => addAttachments(files)}
          onPickSuggestion={(text) => setMessage(text)}
          canSendEmpty={hasCompleted && !isAnyInFlight}
          disabled={isDisabled || isAnyInFlight}
          isStreaming={isStreaming}
          isExploring={isRequestingLocationInfo}
        />
      </Box>

      {/* Hidden native-camera trigger.
          accept="image/*" + capture="environment" routes to the OS
          camera app on mobile. On desktop (or any device without a
          camera) the OS falls back to a file picker. The element is
          always mounted but visually hidden; we click it
          programmatically from the Composer's camera button. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleNativeCameraChange}
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Camera-unavailable dialog. Surfaced when something prevents
          the native flow from working at all. The OS handles its own
          permission errors in its own UI; this dialog is for cases
          we'd otherwise silently swallow. */}
      <Dialog
        open={!!cameraUnavailableMsg}
        onClose={() => setCameraUnavailableMsg(null)}
      >
        <Box sx={{ p: 3, maxWidth: 360 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('Camera unavailable')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {cameraUnavailableMsg}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setCameraUnavailableMsg(null)}>
              {t('OK')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Visit notification popover */}
      {latestVisit && (
        <VisitNotification
          verification={{
            decision: "verified",
            confidence: latestVisit.confidence_score,
            landmark_name: latestVisit.landmark_name,
            landmark_type: latestVisit.landmark_type,
            reasoning: latestVisit.reasoning,
            coordinates_match: latestVisit.coordinates_match,
            visit_worthy: latestVisit.visit_worthy,
          }}
          visitSaved={true}
          visitId={latestVisit.id}
          onClose={clearLatestVisit}
          onViewVisits={() => {
            /* future: navigate to /visits */
          }}
        />
      )}

      {/* PWA install prompt */}
      <Snackbar
        open={showInstallPrompt}
        onClose={() => setShowInstallPrompt(false)}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setShowInstallPrompt(false)}
          >
            <InstallMobile sx={{ mr: 1 }} />
            
            {t("Install App")}
          </Button>
        }
      >
        <Alert severity="info" sx={{ width: "100%" }}>
          
          {t("Install Instant Tours for the best experience")}
        </Alert>
      </Snackbar>
    </Box>
  );
};
